import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, clearOfflineCache } from "@/lib/api";

const PERMS = {
  owner: ["view", "record", "manage", "admin", "billing", "audit"],
  manager: ["view", "record", "manage", "admin", "audit"],
  chef: ["view", "record", "manage", "audit"],
  staff: ["view", "record"],
  auditor: ["view", "audit"],
};

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueIdState] = useState(localStorage.getItem("ka_venue") || "");

  const loadVenues = useCallback(async () => {
    let data;
    try {
      ({ data } = await api.get("/venues"));
      localStorage.setItem("ka_venues", JSON.stringify(data));
    } catch (e) {
      if (e.response) throw e;
      data = JSON.parse(localStorage.getItem("ka_venues") || "[]");
    }
    setVenues(data);
    setVenueIdState((cur) => {
      const ok = data.find((v) => v.id === cur);
      const next = ok ? cur : data[0]?.id || "";
      localStorage.setItem("ka_venue", next);
      return next;
    });
  }, []);

  const applySession = useCallback(async (payload) => {
    localStorage.setItem("ka_session", JSON.stringify(payload));
    localStorage.removeItem("ka_pending_logout");
    setUser(payload.user);
    setBusiness(payload.business);
    if (payload.user.business_id) await loadVenues();
    else setVenues([]);
  }, [loadVenues]);

  const refreshMe = useCallback(async () => {
    if (localStorage.getItem("ka_pending_logout")) {
      await api.post("/auth/logout").then(() => localStorage.removeItem("ka_pending_logout")).catch(() => {});
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      await applySession(data);
    } catch (e) {
      const cached = !e.response && localStorage.getItem("ka_session");
      if (cached) await applySession(JSON.parse(cached));
      else setUser(false);
    }
  }, [applySession]);

  useEffect(() => {
    refreshMe();
    const onLogout = () => setUser(false);
    window.addEventListener("ka-logout", onLogout);
    return () => window.removeEventListener("ka-logout", onLogout);
  }, [refreshMe]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => localStorage.setItem("ka_pending_logout", "1"));
    ["ka_session", "ka_venues"].forEach((k) => localStorage.removeItem(k));
    clearOfflineCache();
    setUser(false);
    setBusiness(null);
  };

  const setVenueId = (id) => {
    localStorage.setItem("ka_venue", id);
    setVenueIdState(id);
  };

  const can = (perm) => !!user && (PERMS[user.tenant_role] || []).includes(perm);
  const venue = venues.find((v) => v.id === venueId);

  return (
    <AuthCtx.Provider value={{ user, business, setBusiness, venues, venue, venueId, setVenueId, loadVenues, applySession, refreshMe, logout, can }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
